import { useEffect, useRef } from 'react';
import './MarkdownRenderer.css';

const MarkdownRenderer = ({ content, documents, onNavigate }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const rendered = parseMarkdown(content);
    containerRef.current.innerHTML = rendered;

    // Handle wikilink clicks
    const wikilinks = containerRef.current.querySelectorAll('.wikilink');
    wikilinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetName = link.getAttribute('data-target');
        
        // Find document by name
        const allDocs = [
          ...documents.analyses || [],
          ...documents.screenplays || [],
          ...documents.notes || [],
          ...documents.essays || []
        ];
        
        const targetDoc = allDocs.find(doc => 
          doc.name.replace(/\.(md|markdown|html|htm|fountain)$/, '') === targetName
        );
        
        if (targetDoc && onNavigate) {
          onNavigate(targetDoc);
        }
      });
    });
  }, [content, documents, onNavigate]);

  return <div ref={containerRef} className="markdown-content" />;
};

// Simple markdown parser
function parseMarkdown(md) {
  let html = md;

  // Process wikilinks [[Link]] before other markdown
  html = html.replace(/\[\[([^\]]+)\]\]/g, (match, text) => {
    return `<a href="#" class="wikilink" data-target="${text}">${text}</a>`;
  });

  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3 id="$1">$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2 id="$1">$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1 id="$1">$1</h1>');

  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.+?)_/g, '<em>$1</em>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2">$1</a>');

  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^\)]+)\)/g, '<img src="$2" alt="$1" />');

  // Code blocks
  html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');

  // Inline code
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Blockquotes
  html = html.replace(/^> (.+)/gm, '<blockquote>$1</blockquote>');

  // Horizontal rules
  html = html.replace(/^\-\-\-$/gm, '<hr>');

  // Lists
  html = html.replace(/^\* (.+)/gm, '<li>$1</li>');
  html = html.replace(/^- (.+)/gm, '<li>$1</li>');
  html = html.replace(/^(\d+)\. (.+)/gm, '<li>$2</li>');

  // Wrap consecutive list items
  html = html.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>');

  // Paragraphs
  html = html.replace(/\n\n/g, '</p><p>');
  html = '<p>' + html + '</p>';

  // Clean up empty paragraphs
  html = html.replace(/<p><\/p>/g, '');
  html = html.replace(/<p>(<h[1-6])/g, '$1');
  html = html.replace(/<\/h[1-6]><\/p>/g, (match) => match.replace('</p>', ''));

  // Footnotes
  html = html.replace(/\[\^(\d+)\]/g, '<sup class="footnote-ref"><a href="#fn$1">$1</a></sup>');

  return html;
}

export default MarkdownRenderer;
